import { isEqual, noop } from 'lodash';
import { memo } from 'react';

import { ModuleTypes } from 'dashboard/apps/DashboardLayoutEditor/constants';
import { Report } from 'dashboard/models/report';
import { ReportTemplate } from 'dashboard/models/report-template';
import { TransactionType } from 'dashboard/models/transaction-type';
import { TransactionModule } from 'dashboard/models/transaction-type-grid-layout';
import { Verification } from 'dashboard/models/verification';
import { createPreviewDocumentGeneric } from 'dashboard/modules/CaseModuleBase/previewBuilders/document';
import { createPreviewEnvironmentProduction } from 'dashboard/modules/CaseModuleBase/previewBuilders/environment';
import { createPreviewInquiry } from 'dashboard/modules/CaseModuleBase/previewBuilders/inquiry';
import { createPreviewRelation } from 'dashboard/modules/CaseModuleBase/previewBuilders/relation';
import {
  createPreviewReportAddressLookup,
  createPreviewReportAdverseMedia,
  createPreviewReportBusinessAdverseMedia,
  createPreviewReportBusinessAssociatedPersons,
  createPreviewReportBusinessClassification,
  createPreviewReportBusinessIndustryClassification,
  createPreviewReportBusinessLiensLookup,
  createPreviewReportBusinessLookup,
  createPreviewReportBusinessNonprofit,
  createPreviewReportBusinessRegistrationsLookup,
  createPreviewReportBusinessSocialMedia,
  createPreviewReportBusinessWatchlist,
  createPreviewReportBusinessWebsite,
  createPreviewReportChainalysisAddressScreening,
  createPreviewReportCryptoAddressWatchlist,
  createPreviewReportCustomList,
  createPreviewReportEmailAddress,
  createPreviewReportFinraBrokerCheck,
  createPreviewReportMxAccount,
  createPreviewReportPhoneNumber,
  createPreviewReportPoliticallyExposedPerson,
  createPreviewReportProfile,
  createPreviewReportProfileNonAuthoritative,
  createPreviewReportSecActionLookup,
  createPreviewReportSentilinkApplicationRisk,
  createPreviewReportSentilinkScores,
  createPreviewReportSocialMedia,
  createPreviewReportSynthetic,
  createPreviewReportWatchlist,
} from 'dashboard/modules/CaseModuleBase/previewBuilders/report';
import {
  createPreviewReportTemplateAddressLookup,
  createPreviewReportTemplateAdverseMedia,
  createPreviewReportTemplateBusinessAdverseMedia,
  createPreviewReportTemplateBusinessAssociatedPersons,
  createPreviewReportTemplateBusinessClassification,
  createPreviewReportTemplateBusinessIndustryClassification,
  createPreviewReportTemplateBusinessLiensLookup,
  createPreviewReportTemplateBusinessLookup,
  createPreviewReportTemplateBusinessNonprofit,
  createPreviewReportTemplateBusinessRegistrationsLookup,
  createPreviewReportTemplateBusinessSocialMedia,
  createPreviewReportTemplateBusinessWatchlist,
  createPreviewReportTemplateBusinessWebsite,
  createPreviewReportTemplateChainalysisAddressScreening,
  createPreviewReportTemplateCryptoAddressWatchlist,
  createPreviewReportTemplateCustomList,
  createPreviewReportTemplateEmailAddress,
  createPreviewReportTemplateFinraBrokerCheck,
  createPreviewReportTemplateMxAccount,
  createPreviewReportTemplatePhoneNumber,
  createPreviewReportTemplatePoliticallyExposedPerson,
  createPreviewReportTemplateProfile,
  createPreviewReportTemplateProfileNonAuthoritative,
  createPreviewReportTemplateSecActionLookup,
  createPreviewReportTemplateSentilinkApplicationRisk,
  createPreviewReportTemplateSentilinkScores,
  createPreviewReportTemplateSocialMedia,
  createPreviewReportTemplateSynthetic,
  createPreviewReportTemplateWatchlist,
} from 'dashboard/modules/CaseModuleBase/previewBuilders/report-template';
import { createPreviewTag } from 'dashboard/modules/CaseModuleBase/previewBuilders/tag';
import { createPreviewTransaction } from 'dashboard/modules/CaseModuleBase/previewBuilders/transactions';
import { createPreviewUser } from 'dashboard/modules/CaseModuleBase/previewBuilders/user';
import {
  createPreviewVerificationAamva,
  createPreviewVerificationBusinessWebsite,
  createPreviewVerificationDatabase,
  createPreviewVerificationDatabaseAadhaar,
  createPreviewVerificationDatabaseBusiness,
  createPreviewVerificationDatabaseEcbsv,
  createPreviewVerificationDatabasePhoneCarrier,
  createPreviewVerificationDatabaseStandard,
  createPreviewVerificationDatabaseTin,
  createPreviewVerificationDatabaseVatNumber,
  createPreviewVerificationDocument,
  createPreviewVerificationGovernmentId,
  createPreviewVerificationPhoneNumber,
  createPreviewVerificationSelfie,
} from 'dashboard/modules/CaseModuleBase/previewBuilders/verification';
import ResourceInquiry from 'dashboard/modules/ResourceInquiry';
import ResourceReport from 'dashboard/modules/ResourceReport';
import ResourceVerification from 'dashboard/modules/ResourceVerification';
import { TransactionModuleBase } from 'dashboard/modules/TransactionModuleBase/TransactionModuleBase';
import { inquiryTemplate } from 'dashboard/views/DashboardInquiryTemplatesView/DashboardInquiryTemplatesView.mocks';
import { FieldSchema } from 'lib/fields';
import { ApiModel } from 'lib/interfaces/api';

interface TransactionModuleBasePreviewProps {
  transactionType: TransactionType;
  fieldSchema: FieldSchema[];
  module: TransactionModule;
}

const reportTemplateAddressLookup = createPreviewReportTemplateAddressLookup();
const reportAddressLookup = createPreviewReportAddressLookup(
  {},
  {
    'report-template': {
      data: { id: reportTemplateAddressLookup.id, type: ApiModel.ReportTemplateAddressLookup },
    },
  },
);

const reportTemplateAdverseMedia = createPreviewReportTemplateAdverseMedia();
const reportAdverseMedia = createPreviewReportAdverseMedia(
  {},
  {
    'report-template': {
      data: { id: reportTemplateAdverseMedia.id, type: ApiModel.ReportTemplateAdverseMedia },
    },
  },
);

const reportTemplateBusinessAdverseMedia = createPreviewReportTemplateBusinessAdverseMedia();
const reportBusinessAdverseMedia = createPreviewReportBusinessAdverseMedia(
  {},
  {
    'report-template': {
      data: {
        id: reportTemplateBusinessAdverseMedia.id,
        type: ApiModel.ReportTemplateBusinessAdverseMedia,
      },
    },
  },
);

const reportTemplateBusinessAssociatedPersons =
  createPreviewReportTemplateBusinessAssociatedPersons();
const reportBusinessAssociatedPersons = createPreviewReportBusinessAssociatedPersons(
  {},
  {
    'report-template': {
      data: {
        id: reportTemplateBusinessAssociatedPersons.id,
        type: ApiModel.ReportTemplateBusinessAssociatedPersons,
      },
    },
  },
);

const ReportTemplateBusinessClassification = createPreviewReportTemplateBusinessClassification();
const reportBusinessClassification = createPreviewReportBusinessClassification(
  {},
  {
    'report-template': {
      data: {
        id: ReportTemplateBusinessClassification.id,
        type: ApiModel.ReportTemplateBusinessClassification,
      },
    },
  },
);

const ReportTemplateBusinessIndustryClassification =
  createPreviewReportTemplateBusinessIndustryClassification();
const reportBusinessIndustryClassification = createPreviewReportBusinessIndustryClassification(
  {},
  {
    'report-template': {
      data: {
        id: ReportTemplateBusinessIndustryClassification.id,
        type: ApiModel.ReportTemplateBusinessIndustryClassification,
      },
    },
  },
);

const reportTemplateBusinessLiensLookup = createPreviewReportTemplateBusinessLiensLookup();
const reportBusinessLiensLookup = createPreviewReportBusinessLiensLookup(
  {},
  {
    'report-template': {
      data: {
        id: reportTemplateBusinessLiensLookup.id,
        type: ApiModel.ReportTemplateBusinessLiensLookup,
      },
    },
  },
);

const reportTemplateBusinessLookup = createPreviewReportTemplateBusinessLookup();
const reportBusinessLookup = createPreviewReportBusinessLookup(
  {},
  {
    'report-template': {
      data: {
        id: reportTemplateBusinessLookup.id,
        type: ApiModel.ReportTemplateBusinessLookup,
      },
    },
  },
);

const reportTemplateBusinessNonprofit = createPreviewReportTemplateBusinessNonprofit();
const reportBusinessNonprofit = createPreviewReportBusinessNonprofit(
  {},
  {
    'report-template': {
      data: {
        id: reportTemplateBusinessNonprofit.id,
        type: ApiModel.ReportTemplateBusinessNonprofit,
      },
    },
  },
);

const reportTemplateBusinessRegistrations =
  createPreviewReportTemplateBusinessRegistrationsLookup();
const reportBusinessRegistrationsLookup = createPreviewReportBusinessRegistrationsLookup(
  {},
  {
    'report-template': {
      data: {
        id: reportTemplateBusinessRegistrations.id,
        type: ApiModel.ReportTemplateBusinessRegistrationsLookup,
      },
    },
  },
);

const reportTemplateBusinessSocialMedia = createPreviewReportTemplateBusinessSocialMedia();
const reportBusinessSocialMedia = createPreviewReportBusinessSocialMedia(
  {},
  {
    'report-template': {
      data: {
        id: reportTemplateBusinessSocialMedia.id,
        type: ApiModel.ReportTemplateBusinessSocialMedia,
      },
    },
  },
);

const reportTemplateBusinessWatchlist = createPreviewReportTemplateBusinessWatchlist();
const reportBusinessWatchlist = createPreviewReportBusinessWatchlist(
  {},
  {
    'report-template': {
      data: {
        id: reportTemplateBusinessWatchlist.id,
        type: ApiModel.ReportTemplateBusinessWatchlist,
      },
    },
  },
);

const reportTemplateBusinessWebsite = createPreviewReportTemplateBusinessWebsite();
const reportBusinessWebsite = createPreviewReportBusinessWebsite(
  {},
  {
    'report-template': {
      data: {
        id: reportTemplateBusinessWebsite.id,
        type: ApiModel.ReportTemplateBusinessWebsite,
      },
    },
  },
);

const reportTemplateChainalysisAddressScreening =
  createPreviewReportTemplateChainalysisAddressScreening();
const reportChainalysisAddressScreening = createPreviewReportChainalysisAddressScreening(
  {},
  {
    'report-template': {
      data: {
        id: reportTemplateChainalysisAddressScreening.id,
        type: ApiModel.ReportTemplateChainalysisAddressScreening,
      },
    },
  },
);

const reportTemplateCryptoAddressWatchlist = createPreviewReportTemplateCryptoAddressWatchlist();
const reportCryptoAddressWatchlist = createPreviewReportCryptoAddressWatchlist(
  {},
  {
    'report-template': {
      data: {
        id: reportTemplateCryptoAddressWatchlist.id,
        type: ApiModel.ReportTemplateCryptoAddressWatchlist,
      },
    },
  },
);

const reportTemplateEmailAddress = createPreviewReportTemplateEmailAddress();
const reportEmailAddress = createPreviewReportEmailAddress(
  {},
  {
    'report-template': {
      data: { id: reportTemplateEmailAddress.id, type: ApiModel.ReportTemplateEmailAddress },
    },
  },
);

const reportTemplateFinraBrokerCheck = createPreviewReportTemplateFinraBrokerCheck();
const reportFinraBrokerCheck = createPreviewReportFinraBrokerCheck(
  {},
  {
    'report-template': {
      data: {
        id: reportTemplateFinraBrokerCheck.id,
        type: ApiModel.ReportTemplateFinraBrokerCheck,
      },
    },
  },
);

const reportTemplateMxAccount = createPreviewReportTemplateMxAccount();
const reportMxAccount = createPreviewReportMxAccount(
  {},
  {
    'report-template': {
      data: {
        id: reportTemplateMxAccount.id,
        type: ApiModel.ReportTemplateMxAccount,
      },
    },
  },
);

const reportTemplatePhoneNumber = createPreviewReportTemplatePhoneNumber();
const reportPhoneNumber = createPreviewReportPhoneNumber(
  {},
  {
    'report-template': {
      data: { id: reportTemplatePhoneNumber.id, type: ApiModel.ReportTemplatePhoneNumber },
    },
  },
);

const reportTemplateProfile = createPreviewReportTemplateProfile();
const reportProfile = createPreviewReportProfile(
  {},
  {
    'report-template': {
      data: { id: reportTemplateProfile.id, type: ApiModel.ReportTemplateProfile },
    },
  },
);

const reportTemplateProfileNonAuthoritative = createPreviewReportTemplateProfileNonAuthoritative();
const reportProfileNonAuthoritative = createPreviewReportProfileNonAuthoritative(
  {},
  {
    'report-template': {
      data: {
        id: reportTemplateProfileNonAuthoritative.id,
        type: ApiModel.ReportTemplateProfileNonAuthoritative,
      },
    },
  },
);

const reportTemplatePoliticallyExposedPerson =
  createPreviewReportTemplatePoliticallyExposedPerson();
const reportPoliticallyExposedPerson = createPreviewReportPoliticallyExposedPerson(
  {},
  {
    'report-template': {
      data: {
        id: reportTemplatePoliticallyExposedPerson.id,
        type: ApiModel.ReportTemplatePoliticallyExposedPerson,
      },
    },
  },
);

const reportTemplateSecActionLookup = createPreviewReportTemplateSecActionLookup();
const reportSecActionLookup = createPreviewReportSecActionLookup(
  {},
  {
    'report-template': {
      data: {
        id: reportTemplateSecActionLookup.id,
        type: ApiModel.ReportTemplateSecActionLookup,
      },
    },
  },
);

const reportTemplateSentilinkApplicationRisk =
  createPreviewReportTemplateSentilinkApplicationRisk();
const reportSentilinkApplicationRisk = createPreviewReportSentilinkApplicationRisk(
  {},
  {
    'report-template': {
      data: {
        id: reportTemplateSentilinkApplicationRisk.id,
        type: ApiModel.ReportTemplateSentilinkApplicationRisk,
      },
    },
  },
);

const reportTemplateSentilinkScores = createPreviewReportTemplateSentilinkScores();
const reportSentilinkScores = createPreviewReportSentilinkScores(
  {},
  {
    'report-template': {
      data: {
        id: reportTemplateSentilinkScores.id,
        type: ApiModel.ReportTemplateSentilinkScores,
      },
    },
  },
);

const reportTemplateSocialMedia = createPreviewReportTemplateSocialMedia();
const reportSocialMedia = createPreviewReportSocialMedia(
  {},
  {
    'report-template': {
      data: { id: reportTemplateSocialMedia.id, type: ApiModel.ReportTemplateSocialMedia },
    },
  },
);

const reportTemplateSynthetic = createPreviewReportTemplateSynthetic();
const reportSynthetic = createPreviewReportSynthetic(
  {},
  {
    'report-template': {
      data: { id: reportTemplateSynthetic.id, type: ApiModel.ReportTemplateSynthetic },
    },
  },
);

const reportTemplateWatchlist = createPreviewReportTemplateWatchlist();
const reportWatchlist = createPreviewReportWatchlist(
  {},
  {
    'report-template': {
      data: { id: reportTemplateWatchlist.id, type: ApiModel.ReportTemplateWatchlist },
    },
  },
);

const reportTemplateCustomList = createPreviewReportTemplateCustomList();
const reportCustomList = createPreviewReportCustomList(
  {},
  {
    'report-template': {
      data: { id: reportTemplateCustomList.id, type: ApiModel.ReportTemplateCustomList },
    },
  },
);

const reportsData: Partial<Record<ApiModel, { report: Report; reportTemplate: ReportTemplate }>> = {
  [ApiModel.ReportTemplateAddressLookup]: {
    report: reportAddressLookup,
    reportTemplate: reportTemplateAddressLookup,
  },
  [ApiModel.ReportTemplateAdverseMedia]: {
    report: reportAdverseMedia,
    reportTemplate: reportTemplateAdverseMedia,
  },
  [ApiModel.ReportTemplateBusinessAdverseMedia]: {
    report: reportBusinessAdverseMedia,
    reportTemplate: reportTemplateBusinessAdverseMedia,
  },
  [ApiModel.ReportTemplateBusinessAssociatedPersons]: {
    report: reportBusinessAssociatedPersons,
    reportTemplate: reportTemplateBusinessAssociatedPersons,
  },
  [ApiModel.ReportTemplateBusinessClassification]: {
    report: reportBusinessClassification,
    reportTemplate: ReportTemplateBusinessClassification,
  },
  [ApiModel.ReportTemplateBusinessIndustryClassification]: {
    report: reportBusinessIndustryClassification,
    reportTemplate: ReportTemplateBusinessIndustryClassification,
  },
  [ApiModel.ReportTemplateBusinessLiensLookup]: {
    report: reportBusinessLiensLookup,
    reportTemplate: reportTemplateBusinessLiensLookup,
  },
  [ApiModel.ReportTemplateBusinessLookup]: {
    report: reportBusinessLookup,
    reportTemplate: reportTemplateBusinessLookup,
  },
  [ApiModel.ReportTemplateBusinessNonprofit]: {
    report: reportBusinessNonprofit,
    reportTemplate: reportTemplateBusinessNonprofit,
  },
  [ApiModel.ReportTemplateBusinessRegistrationsLookup]: {
    report: reportBusinessRegistrationsLookup,
    reportTemplate: reportTemplateBusinessRegistrations,
  },
  [ApiModel.ReportTemplateBusinessSocialMedia]: {
    report: reportBusinessSocialMedia,
    reportTemplate: reportTemplateBusinessSocialMedia,
  },
  [ApiModel.ReportTemplateBusinessWatchlist]: {
    report: reportBusinessWatchlist,
    reportTemplate: reportTemplateBusinessWatchlist,
  },
  [ApiModel.ReportTemplateBusinessWebsite]: {
    report: reportBusinessWebsite,
    reportTemplate: reportTemplateBusinessWebsite,
  },
  [ApiModel.ReportTemplateChainalysisAddressScreening]: {
    report: reportChainalysisAddressScreening,
    reportTemplate: reportTemplateChainalysisAddressScreening,
  },
  [ApiModel.ReportTemplateCryptoAddressWatchlist]: {
    report: reportCryptoAddressWatchlist,
    reportTemplate: reportTemplateCryptoAddressWatchlist,
  },
  [ApiModel.ReportTemplateEmailAddress]: {
    report: reportEmailAddress,
    reportTemplate: reportTemplateEmailAddress,
  },
  [ApiModel.ReportTemplateFinraBrokerCheck]: {
    report: reportFinraBrokerCheck,
    reportTemplate: reportTemplateFinraBrokerCheck,
  },
  [ApiModel.ReportTemplateMxAccount]: {
    report: reportMxAccount,
    reportTemplate: reportTemplateMxAccount,
  },
  [ApiModel.ReportTemplatePhoneNumber]: {
    report: reportPhoneNumber,
    reportTemplate: reportTemplatePhoneNumber,
  },
  [ApiModel.ReportTemplatePoliticallyExposedPerson]: {
    report: reportPoliticallyExposedPerson,
    reportTemplate: reportTemplatePoliticallyExposedPerson,
  },
  [ApiModel.ReportTemplateProfile]: {
    report: reportProfile,
    reportTemplate: reportTemplateProfile,
  },
  [ApiModel.ReportTemplateProfileNonAuthoritative]: {
    report: reportProfileNonAuthoritative,
    reportTemplate: reportTemplateProfileNonAuthoritative,
  },
  [ApiModel.ReportTemplateSecActionLookup]: {
    report: reportSecActionLookup,
    reportTemplate: reportTemplateSecActionLookup,
  },
  [ApiModel.ReportTemplateSentilinkApplicationRisk]: {
    report: reportSentilinkApplicationRisk,
    reportTemplate: reportTemplateSentilinkApplicationRisk,
  },
  [ApiModel.ReportTemplateSentilinkScores]: {
    report: reportSentilinkScores,
    reportTemplate: reportTemplateSentilinkScores,
  },
  [ApiModel.ReportTemplateSocialMedia]: {
    report: reportSocialMedia,
    reportTemplate: reportTemplateSocialMedia,
  },
  [ApiModel.ReportTemplateSynthetic]: {
    report: reportSynthetic,
    reportTemplate: reportTemplateSynthetic,
  },
  [ApiModel.ReportTemplateWatchlist]: {
    report: reportWatchlist,
    reportTemplate: reportTemplateWatchlist,
  },
  [ApiModel.ReportCustomList]: {
    report: reportCustomList,
    reportTemplate: reportTemplateCustomList,
  },
};

const verificationGovId = createPreviewVerificationGovernmentId();
const verificationSelfie = createPreviewVerificationSelfie();

const verificationsData: Partial<Record<ApiModel, Verification>> = {
  [ApiModel.VerificationAamva]: createPreviewVerificationAamva(),
  [ApiModel.VerificationBusinessWebsite]: createPreviewVerificationBusinessWebsite(),
  [ApiModel.VerificationDatabase]: createPreviewVerificationDatabase(),
  [ApiModel.VerificationDatabaseAadhaar]: createPreviewVerificationDatabaseAadhaar(),
  [ApiModel.VerificationDatabaseBusiness]: createPreviewVerificationDatabaseBusiness(),
  [ApiModel.VerificationDatabaseEcbsv]: createPreviewVerificationDatabaseEcbsv(),
  [ApiModel.VerificationDatabasePhoneCarrier]: createPreviewVerificationDatabasePhoneCarrier(),
  [ApiModel.VerificationDatabaseStandard]: createPreviewVerificationDatabaseStandard(),
  [ApiModel.VerificationDatabaseTin]: createPreviewVerificationDatabaseTin(),
  [ApiModel.VerificationDatabaseVatNumber]: createPreviewVerificationDatabaseVatNumber(),
  [ApiModel.VerificationDocument]: createPreviewVerificationDocument(),
  [ApiModel.VerificationGovernmentId]: verificationGovId,
  [ApiModel.VerificationPhoneNumber]: createPreviewVerificationPhoneNumber(),
  [ApiModel.VerificationSelfie]: verificationSelfie,
};
const TransactionModuleBasePreview = ({
  module,
  transactionType,
}: TransactionModuleBasePreviewProps) => {
  const currentEnvironment = createPreviewEnvironmentProduction();
  const transactionTags = [
    createPreviewTag({ name: 'tag 1' }),
    createPreviewTag({ name: 'tag 2' }),
  ];
  const user = createPreviewUser();
  const transaction = createPreviewTransaction();

  const relations = transactionType.attributes['relation-schemas'].reduce(
    (relationsSoFar, schema) => ({
      ...relationsSoFar,
      ...createPreviewRelation(schema),
    }),
    {},
  );
  const inquiry = createPreviewInquiry({}, { 'inquiry-template': { data: inquiryTemplate } });
  const document = createPreviewDocumentGeneric();
  const verificationGovId = createPreviewVerificationGovernmentId();
  const verificationSelfie = createPreviewVerificationSelfie();
  const inquiryTag = createPreviewTag({ name: 'inquiry tag' });
  const reportTag = createPreviewTag({ name: 'report tag' });

  switch (module.type) {
    case ModuleTypes.Resource:
      switch (module['field-key']) {
        case 'inquiries':
          return (
            <ResourceInquiry
              attributes={module.attributes}
              hideEmptyAttributes={module['hide-empty-attributes']}
              inquiry={inquiry}
              documents={[document]}
              tags={[inquiryTag]}
              inquiryTemplates={[inquiryTemplate]}
              inquiryTemplateVersions={[]}
              verifications={[verificationGovId, verificationSelfie]}
              defaultAccordionOpen={module['default-accordion-open'] ?? true}
              featureFlags={{}}
              handleUpdateInquiryTags={() => undefined}
              handleFlagInquiryIssues={() => Promise.resolve()}
            />
          );
        case 'reports':
          return (
            <ResourceReport
              report={reportsData[module['report-template-type']]?.report as Report}
              reportTemplate={
                reportsData[module['report-template-type']]?.reportTemplate as ReportTemplate
              }
              tags={[reportTag]}
              defaultAccordionOpen={module['default-accordion-open'] ?? true}
              featureFlags={{}}
              handleUpdateReportTags={() => undefined}
            />
          );
        case 'verifications':
          return (
            <ResourceVerification
              verification={verificationsData[module['verification-type']] as Verification}
              documents={[document]}
              defaultAccordionOpen={module['default-accordion-open'] ?? true}
              featureFlags={{}}
              handleFlagVerificationIssues={() => Promise.resolve()}
            />
          );
        default:
          return null;
      }
    default:
      return (
        <TransactionModuleBase
          module={module}
          transactionApi={{
            transaction: transaction,
            transactionType: transactionType,
            currentEnvironment: currentEnvironment,
            transactionVariableDefinitions: [],
            tags: transactionTags,
            users: [user],
            lists: {},
            listItems: {},
            listMatches: {},
            handleUpdateTags: noop,
            relations,
          }}
          isPreview
        />
      );
  }
};
const MemoizedTransactionModuleBasePreview = memo(
  TransactionModuleBasePreview,
  (prevProps, nextProps) => {
    // Compare all props except the position data on the module to avoid rerendering when modules move
    const {
      fieldSchema: prevFieldSchema,
      module: { position: prevModulePosition, ...restPrevModule },
    } = prevProps;
    const {
      fieldSchema: nextFieldSchema,
      module: { position: nextModulePosition, ...restNextModule },
    } = nextProps;
    const isPropsEqual =
      isEqual(nextFieldSchema, prevFieldSchema) && isEqual(restPrevModule, restNextModule);
    return isPropsEqual;
  },
);

export default MemoizedTransactionModuleBasePreview;
